// react
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

// router
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextProject, ContextUsersPermissions } from '../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2/ProjectV2.context';

// hooks
import { useCondition } from '../../../hooks/useConditions';
import { useFetch } from '../../../hooks/useFetch';

// components
import { PageDetailSkeleton } from '../../ui/PageDetailSkeleton/PageDetailSkeleton';
import { CardDetail } from '../../cards/CardDetail/CardDetail';
import { CardInvoiceDetails } from '../../cards/CardInvoiceDetails/CardInvoiceDetails';
import {
	getStatusColor,
	getStatusLabel,
	HeaderDetailPage,
} from '../../ui/HeaderDetailPage/HeaderDetailPage';

// mui
import { alpha, Box, Chip, Divider, Grid, Stack, Typography, useTheme } from '@mui/material';

// icons
import {
	AccountTree as IconActivity,
	Cancel as IconCancel,
	CheckCircle as IconCheckCircle,
	Description as IconContract,
	GppGood as IconGate,
	History as IconHistory,
	HourglassEmpty as IconPending,
	Assignment as IconWorkPlan,
	Link as IconLink,
	Notes as IconNotes,
	OpenInNew as IconOpenInNew,
	Payment as IconPayment,
	Schedule as IconSchedule,
	Send as IconSend,
	Verified as IconVerified,
	Group as IconGroup,
	Extension as IconComponent,
	Category as IconCategory,
	Receipt as IconExpenseType,
} from '@mui/icons-material';

// dialogs
import { DialogAddCondition } from '../../dialogs/DialogAddCondition/DialogAddCondition';

// types
import { Condition } from '../../../@types';

// services

// components
import VoucherPaymentReconciliation from '../../ui/VoucherPaymentReconciliation/VoucherPaymentReconciliation';

// utils
import { formatCurrency } from '../../../utils/currency';
import {
	getContractCommitmentAmount,
	getContractNum,
	getContractSupplierName,
} from '../ProjectV2/Contracts/Contracts.utils';
import { COLORS, GRADIENTS } from '../../../data';

// Field type display helpers (shared concept with FormAddConditionWizard)
const FIELD_TYPE_COLORS: Record<string, string> = {
	document: '#1976d2',
	yesno: '#7b1fa2',
	text: '#546e7a',
	number: '#00897b',
	date: '#e65100',
	select: '#5c6bc0',
};

const FIELD_TYPE_LABELS: Record<string, string> = {
	document: 'Document',
	yesno: 'Yes / No',
	text: 'Text',
	number: 'Number',
	date: 'Date',
	select: 'Dropdown',
};

export const PageCondition = () => {
	// hooks
	const { t } = useTranslation();
	const navigate = useNavigate();
	const theme = useTheme();
	const { conditionId } = useParams<{ conditionId: string }>();
	const [searchParams, setSearchParams] = useSearchParams();
	const conditionIdNumber = Number(conditionId);

	// Fetch condition by ID
	const {
		data: condition,
		loading: conditionLoading,
		refetch,
	} = useCondition({
		id: conditionIdNumber,
		isEnabled: Number.isFinite(conditionIdNumber),
		query: { populate: 'workPlan,activity,contract,verifiedBy,component,category,expenseType' },
	});

	// Track dialog state for refetch on close
	const dialogParam = searchParams.get('dialog');
	const prevDialogRef = useRef<string | null>(dialogParam);

	// Refetch data when dialog closes (dialog param goes from value to null)
	useEffect(() => {
		if (prevDialogRef.current && !dialogParam) {
			// Dialog was open and is now closed - refetch data
			refetch();
		}
		prevDialogRef.current = dialogParam;
	}, [dialogParam, refetch]);

	// context
	const { project } = useContext(ContextProject);
	const { activities, contracts, workPlans, vouchers, getInvoicesByContract } =
		useContext(ContextSpendPlan);
	const { usersPermissions: projectUsers } = useContext(ContextUsersPermissions);

	// Independent fetch for ALL conditions (shared context may be paginated)
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');
	const [allProjectConditions, setAllProjectConditions] = useState<Condition[]>([]);
	useFetch({
		isEnabled: Boolean(project?.id),
		onSuccess: (res) => {
			if (res.data) setAllProjectConditions(res.data);
		},
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		url: `${process.env.REACT_APP_API_URL?.replace(/\/v1\/?$/, '')}/v2/conditions?project=${project?.id}&populate=component,category,expenseType&orderBy=sequenceOrder&order=ASC`,
	});

	// state
	// (Verification status changes happen via the voucher flow — see PageVoucher.
	// This page is read-only with respect to status transitions.)

	// handlers
	const handleEdit = () => {
		if (!condition) return;
		if (contract && contract.status !== 'DRAFT') {
			// Maker-checker gate: a milestone on a contract can only be
			// changed while the contract is in DRAFT. Bail silently — the
			// edit affordance is hidden in this state anyway.
			return;
		}
		const params = new URLSearchParams(searchParams);
		params.set('dialog', 'editCondition');
		params.set('dialogCondition', String(condition.id));
		setSearchParams(params);
	};

	const handleOpenContractPage = (contractId: number) => {
		navigate(`/project/${project?.idWbg}/spend-plans/contract/${contractId}`);
	};

	const handleOpenWorkPlanPage = (workPlanId: number) => {
		navigate(`/project/${project?.idWbg}/spend-plans/work-planning/work-plan/${workPlanId}`);
	};

	const handleOpenActivityPage = (activityId: number) => {
		navigate(`/project/${project?.idWbg}/spend-plans/activity/${activityId}`);
	};

	// Get work plan for this condition
	const workPlan = useMemo(() => {
		if (!condition) return null;
		const workPlanId =
			typeof condition.workPlan === 'number' ? condition.workPlan : condition.workPlan?.id;
		return workPlanId ? workPlans.find((e) => e.id === workPlanId) || null : null;
	}, [condition, workPlans]);

	// Get activity for this condition
	const activity = useMemo(() => {
		if (!condition) return null;
		const activityId =
			typeof condition.activity === 'number' ? condition.activity : condition.activity?.id;
		return activityId ? activities.find((a) => a.id === activityId) || null : null;
	}, [condition, activities]);

	// Get contract for this condition
	// Note: When populated, contract object uses 'contractId' not 'id'
	const contract = useMemo(() => {
		if (!condition) return null;
		const contractId =
			typeof condition.contract === 'number'
				? condition.contract
				: condition.contract?.id || (condition.contract as { contractId?: number })?.contractId;
		return contractId ? contracts.find((c) => c.id === contractId) || null : null;
	}, [condition, contracts]);

	// Get currency for this condition's payment amounts.
	//
	// Payment/milestone amounts are denominated in the CONTRACT's own currency
	// at entry time (see FormAddConditionWizard, which uses
	// `contract.amounts[0].currency` and resolves the contract value in that
	// currency). The detail page must display them in that same currency —
	// using the work plan's reporting currency here mislabels a local-currency
	// figure (e.g. shows "$5,000" for a payment that is actually ETB 5,000) and
	// makes percentage conditions resolve to 0 when the contract has no amount
	// in the work-plan currency.
	const currency = useMemo(() => {
		if (contract?.amounts?.[0]?.currency) return contract.amounts[0].currency;
		if (workPlan?.currency) return workPlan.currency;
		if (contract) {
			const contractWorkPlanId = contract.workPlan;
			const contractWorkPlan = contractWorkPlanId
				? workPlans.find((e) => e.id === contractWorkPlanId)
				: null;
			return contractWorkPlan?.currency || 'USD';
		}
		return 'USD';
	}, [contract, workPlan, workPlans]);

	// Contract value resolved in the same (contract) currency as the payment
	// amounts, so a percentage condition evaluates against the amount that
	// actually exists on the contract. Null when the contract has no amount in
	// that currency, so the derived payment amount falls back to 0 below.
	const contractValue = useMemo(() => {
		if (!contract) return null;
		return getContractCommitmentAmount(contract, { currency });
	}, [contract, currency]);

	// Calculate payment amount from percentage if needed
	const calculatedPaymentAmount = useMemo(() => {
		if (condition?.paymentAmount) return condition.paymentAmount;
		if (condition?.paymentPercentage && contractValue != null && contractValue > 0) {
			return contractValue * (condition.paymentPercentage / 100);
		}
		return 0;
	}, [condition?.paymentAmount, condition?.paymentPercentage, contractValue]);

	// Find vouchers for this condition with their data request responses
	const conditionVouchers = useMemo(() => {
		if (!condition) return [];
		return vouchers.filter((v) => {
			const condId =
				typeof v.condition === 'number' ? v.condition : (v.condition as { id?: number })?.id;
			return condId === condition.id;
		});
	}, [condition, vouchers]);

	// Find template conditions matching this condition's activity classification
	// Uses independently-fetched conditions to avoid shared context pagination limits
	const templateConditions = useMemo(() => {
		if (!condition || !activity) return [];

		// Resolve activity's classification IDs
		const actComponentId =
			typeof activity.component === 'number'
				? activity.component
				: (activity.component as { id?: number })?.id;
		const actCategoryId =
			typeof activity.category === 'number'
				? activity.category
				: (activity.category as { id?: number })?.id;
		const actExpenseTypeId =
			typeof activity.expenseType === 'number'
				? activity.expenseType
				: (activity.expenseType as { id?: number })?.id;

		return allProjectConditions.filter((c) => {
			if (c.id === condition.id) return false;
			if (c.type !== 'TEMPLATE') return false;
			// Must have at least one verification section with fields
			const sections = c.verificationSections || [];
			if (!sections.some((s) => (s.fields || []).length > 0)) return false;
			// Match by classification — template must relate to one of the activity's entities
			const tCompId =
				typeof c.component === 'number' ? c.component : (c.component as { id?: number })?.id;
			const tCatId =
				typeof c.category === 'number' ? c.category : (c.category as { id?: number })?.id;
			const tEtId =
				typeof c.expenseType === 'number' ? c.expenseType : (c.expenseType as { id?: number })?.id;
			if (tCompId && actComponentId && tCompId === actComponentId) return true;
			if (tCatId && actCategoryId && tCatId === actCategoryId) return true;
			if (tEtId && actExpenseTypeId && tEtId === actExpenseTypeId) return true;
			return false;
		});
	}, [condition, allProjectConditions, activity]);

	// Not found state (only after loading is complete)
	if (!conditionLoading && !condition) {
		return (
			<Box sx={{ px: 3, py: 4 }}>
				<Typography>{t('spendPlan.condition.notFound')}</Typography>
			</Box>
		);
	}

	// Status info helper
	const getStatusInfo = (status: string) => {
		switch (status) {
			case 'APPROVED':
				return {
					color: COLORS.success.main,
					icon: IconCheckCircle,
					label: t('spendPlan.condition.status.paid'),
				};
			case 'VERIFIED':
				return {
					color: COLORS.info.main,
					icon: IconVerified,
					label: t('spendPlan.condition.status.readyToPay'),
				};
			case 'SUBMITTED':
				return {
					color: COLORS.warning.main,
					icon: IconSchedule,
					label: t('spendPlan.condition.status.underReview'),
				};
			case 'REJECTED':
				return { color: theme.palette.error.main, icon: IconCancel, label: t('rejected') };
			default:
				return { color: theme.palette.grey[400], icon: IconPending, label: 'Not Started' };
		}
	};

	// Derive an effective status: if the condition is still in its default
	// "PENDING"/empty state but vouchers have already been issued against it,
	// reflect the voucher progression in the header chip rather than showing
	// "Not Started" (Story #88 follow-up).
	const effectiveConditionStatus = (() => {
		if (!condition) return 'PENDING';
		const raw = condition.status;
		if (raw && raw !== 'PENDING') return raw;
		if (conditionVouchers.length === 0) return raw || 'PENDING';
		const vStatuses = conditionVouchers.map((v) => v.status);
		if (vStatuses.length > 0 && vStatuses.every((s) => s === 'REDEEMED')) return 'APPROVED';
		if (vStatuses.some((s) => ['VERIFIED', 'READY', 'GENERATED', 'REDEEMED'].includes(s as string)))
			return 'VERIFIED';
		return raw || 'PENDING';
	})();

	const statusInfo = getStatusInfo(effectiveConditionStatus);

	// Determine if this is a payment condition
	const isPaymentCondition = condition?.conditionType === 'payment';

	// Build stats for HeaderDetailPage (only when condition is loaded)
	const headerStats = condition
		? [
				// Status
				{
					label: t('status'),
					value: (
						<Chip
							label={statusInfo.label}
							size="small"
							sx={{
								bgcolor: alpha(statusInfo.color, 0.15),
								color: statusInfo.color,
								fontSize: { xs: '1rem', sm: '1.125rem' },
								fontWeight: 600,
								height: 28,
								px: 1,
							}}
						/>
					),
				},
				// Sequence
				{ label: t('spendPlan.sequence'), value: `#${condition.sequenceOrder || 1}` },
				// Purpose
				...(condition.type
					? [
							{
								label: t('spendPlan.condition.purpose'),
								value: condition.type.charAt(0) + condition.type.slice(1).toLowerCase(),
							},
						]
					: []),
				// Condition Type (Payment or Base)
				{
					label: t('spendPlan.condition.conditionType'),
					value: isPaymentCondition
						? t('spendPlan.condition.type.payment')
						: t('spendPlan.condition.type.base'),
				},
				// Milestone Type (if set)
				...(condition.milestoneType
					? [
							{
								label: 'Milestone',
								value:
									condition.milestoneType.charAt(0) +
									condition.milestoneType.slice(1).toLowerCase(),
							},
						]
					: []),
				// Required
				{ label: t('required'), value: condition.isRequired ? t('yes') : t('no') },
				// Payment Percentage (if payment condition)
				...(isPaymentCondition && condition.paymentPercentage
					? [
							{
								label: t('spendPlan.condition.paymentPercentage'),
								value: `${condition.paymentPercentage}%`,
							},
						]
					: []),
				// Payment Amount (if payment condition)
				...(isPaymentCondition && calculatedPaymentAmount > 0
					? [
							{
								label: t('spendPlan.condition.calculatedAmount'),
								value: formatCurrency({ currency, value: calculatedPaymentAmount }),
							},
						]
					: []),
				// Verified Date
				...(condition.verifiedAt
					? [
							{
								label: t('spendPlan.condition.verified'),
								value: new Date(condition.verifiedAt).toLocaleDateString(),
							},
						]
					: []),
			]
		: [];

	return (
		<Box>
			{/* Header - Full width, always visible with loading state */}
			<HeaderDetailPage
				loading={conditionLoading}
				loadingActions={1}
				icon={IconGate}
				breadcrumb={{
					label: 'Payment Conditions',
					to: `/project/${project?.idWbg}/spend-plans/payment-conditions`,
				}}
				title={condition?.name || ''}
				variant="condition"
				stats={headerStats}
				onEdit={
					!conditionLoading && condition && (!contract || contract.status === 'DRAFT')
						? handleEdit
						: undefined
				}
			/>

			{/* Content with padding */}
			<Box sx={{ px: 3, pt: 2, pb: 3 }}>
				{/* Show skeleton for content while loading */}
				{conditionLoading ? (
					<PageDetailSkeleton leftCards={2} rightCards={2} />
				) : condition ? (
					<>
						{/* Main Layout: Left (8/12) + Right (4/12) columns */}
						<Grid container spacing={2}>
							{/* Left Column: Description + Documents */}
							<Grid item xs={12} lg={8}>
								<Stack spacing={2}>
									{/* Description Card (with History) */}
									<CardDetail
										icon={IconNotes}
										title={t('description')}
										variant="condition"
										themeColor={GRADIENTS.condition.icon}>
										<Box sx={{ p: 2 }}>
											{condition.description ? (
												<Typography color="text.secondary" variant="body2">
													{condition.description}
												</Typography>
											) : (
												<Typography
													color="text.secondary"
													variant="body2"
													sx={{ fontStyle: 'italic' }}>
													{t('spendPlan.noDescription')}
												</Typography>
											)}
											{/* History dates */}
											{(condition.created || condition.modified) && (
												<Stack
													direction="row"
													spacing={3}
													sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'grey.100' }}>
													{condition.created && (
														<Stack direction="row" spacing={0.5} alignItems="center">
															<IconHistory sx={{ fontSize: 14, color: 'text.disabled' }} />
															<Typography variant="caption" color="text.secondary">
																{t('created')}
															</Typography>
															<Typography variant="caption" sx={{ fontWeight: 500 }}>
																{new Date(condition.created).toLocaleDateString()}
															</Typography>
														</Stack>
													)}
													{condition.modified && (
														<Stack direction="row" spacing={0.5} alignItems="center">
															<IconHistory sx={{ fontSize: 14, color: 'text.disabled' }} />
															<Typography variant="caption" color="text.secondary">
																{t('spendPlan.lastModified')}
															</Typography>
															<Typography variant="caption" sx={{ fontWeight: 500 }}>
																{new Date(condition.modified).toLocaleDateString()}
															</Typography>
														</Stack>
													)}
												</Stack>
											)}
										</Box>
									</CardDetail>

									{/* Invoice card(s) linked to this milestone */}
									{contract &&
										getInvoicesByContract(contract.id)
											.filter((inv) => inv.milestoneNumber === condition.id)
											.map((inv) => (
												<CardInvoiceDetails key={inv.id} invoice={inv} />
											))}

									{/* Requirements Card */}
									{condition.verificationSections && condition.verificationSections.length > 0 && (
										<CardDetail
											icon={IconGroup}
											title="Requirements"
											variant="condition"
											themeColor={GRADIENTS.condition.icon}>
											<Box sx={{ p: 2 }}>
												{condition.verificationSections.map((section, sIdx) => {
													const assignee = projectUsers.find((u) => u.id === section.assignedTo);
													// If the section is still PENDING but vouchers have been
													// issued against this condition, treat the section as
													// VERIFIED for display purposes (Story #88 follow-up).
													const effectiveSectionStatus =
														(!section.status || section.status === 'PENDING') &&
														conditionVouchers.length > 0
															? 'VERIFIED'
															: section.status || 'PENDING';
													const statusColor =
														effectiveSectionStatus === 'VERIFIED'
															? COLORS.success.main
															: effectiveSectionStatus === 'REJECTED'
																? theme.palette.error.main
																: theme.palette.grey[500];
													const sectionFields = section.fields || [];
													return (
														<Box key={section.id}>
															{sIdx > 0 && <Divider sx={{ opacity: 0.15, mx: 2, my: 0.5 }} />}
															<Box
																sx={{
																	display: 'flex',
																	alignItems: 'center',
																	gap: 0.75,
																	px: 1,
																	pt: 0.75,
																	pb: 0.25,
																}}>
																<IconGroup sx={{ fontSize: 16, color: 'primary.main' }} />
																<Typography
																	sx={{
																		fontSize: '1.1rem',
																		fontWeight: 700,
																		color: theme.palette.grey[500],
																		textTransform: 'uppercase',
																		letterSpacing: '0.1em',
																	}}>
																	{section.name || `Section ${sIdx + 1}`}
																</Typography>
																{assignee && (
																	<Chip
																		label={`${assignee.firstName} ${assignee.lastName}`}
																		size="small"
																		variant="outlined"
																		sx={{ height: 24, fontSize: '1.1rem' }}
																	/>
																)}
																<Typography
																	sx={{
																		fontSize: '1.1rem',
																		fontWeight: 700,
																		color: theme.palette.grey[500],
																	}}>
																	{sectionFields.length}
																</Typography>
																<Chip
																	label={getStatusLabel(effectiveSectionStatus, t)}
																	size="small"
																	variant="outlined"
																	sx={{
																		height: 24,
																		fontSize: '1.1rem',
																		color: statusColor,
																		borderColor: statusColor,
																	}}
																/>
															</Box>
															{sectionFields.map((field) => {
																const accentColor = FIELD_TYPE_COLORS[field.fieldType] || '#546e7a';
																return (
																	<Stack
																		key={field.id}
																		direction="row"
																		alignItems="flex-start"
																		spacing={1}
																		sx={{
																			py: 0.5,
																			px: 2,
																			pl: 3,
																		}}>
																		<Box
																			sx={{
																				width: 8,
																				height: '1.75rem',
																				display: 'flex',
																				alignItems: 'center',
																				flexShrink: 0,
																			}}>
																			<Box
																				sx={{
																					width: 8,
																					height: 8,
																					borderRadius: '50%',
																					bgcolor: alpha(accentColor, 0.4),
																				}}
																			/>
																		</Box>
																		<Typography
																			sx={{
																				fontSize: '1.25rem',
																				lineHeight: '1.75rem',
																				fontWeight: 500,
																				color: '#374151',
																				flex: 1,
																				minWidth: 0,
																			}}>
																			{field.label || FIELD_TYPE_LABELS[field.fieldType]}
																		</Typography>
																		<Stack
																			direction="row"
																			alignItems="center"
																			spacing={1}
																			sx={{ height: '1.75rem', flexShrink: 0 }}>
																			<Typography sx={{ color: '#D1D5DB', fontSize: '1.25rem' }}>
																				—
																			</Typography>
																			<Typography
																				sx={{
																					fontSize: '1.25rem',
																					color: '#D1D5DB',
																					fontStyle: 'italic',
																				}}>
																				{field.required ? 'Required' : 'Optional'}
																			</Typography>
																			<Typography
																				sx={{
																					fontSize: '1.25rem',
																					color: accentColor,
																					fontWeight: 600,
																					opacity: 0.7,
																				}}>
																				{FIELD_TYPE_LABELS[field.fieldType] || field.fieldType}
																			</Typography>
																			{field.required ? (
																				<Box
																					sx={{
																						width: 16,
																						height: 16,
																						borderRadius: '50%',
																						border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
																					}}
																				/>
																			) : (
																				<Box sx={{ width: 16 }} />
																			)}
																		</Stack>
																	</Stack>
																);
															})}
														</Box>
													);
												})}
											</Box>
										</CardDetail>
									)}

									{/*
									 * Additional Requirements — preconfigured requirement schemas for
									 * components, categories, or expense types.
									 *
									 * Suppressed when viewing a payment milestone: matching template
									 * sections are already composed inline into the milestone's own
									 * `verificationSections` (see backend composeTemplateSections),
									 * and rendering them again here would duplicate the same content.
									 * Still shown for TEMPLATE / base condition views, where it acts
									 * as a sibling-reference card.
									 */}
									{condition.conditionType !== 'payment' && templateConditions.length > 0 && (
										<CardDetail
											icon={IconGate}
											title="Additional Requirements"
											subtitle="For reference, based on classification of linked activity"
											variant="condition"
											themeColor="#8b5cf6">
											<Box sx={{ py: 1 }}>
												{templateConditions.map((ic, icIdx) => {
													const icSections = ic.verificationSections || [];
													if (icSections.length === 0) return null;
													return (
														<Box key={ic.id}>
															{icIdx > 0 && <Divider sx={{ mx: 2, my: 1, opacity: 0.3 }} />}
															{/* Inherited condition name header */}
															<Box
																sx={{
																	display: 'flex',
																	alignItems: 'center',
																	gap: 0.75,
																	px: 2,
																	pt: 1,
																	pb: 0.5,
																}}>
																<IconGate sx={{ fontSize: 16, color: '#8b5cf6' }} />
																<Typography
																	sx={{
																		fontSize: '1.2rem',
																		fontWeight: 700,
																		color: '#8b5cf6',
																	}}>
																	{ic.name}
																</Typography>
																<Chip
																	label="Additional Requirement"
																	size="small"
																	sx={{
																		height: 20,
																		fontSize: '1.2rem',
																		bgcolor: alpha('#8b5cf6', 0.12),
																		color: '#8b5cf6',
																		fontWeight: 600,
																	}}
																/>
															</Box>
															{/* Sections and fields from this additional requirement */}
															{icSections.map((section, sIdx) => {
																const assignee = projectUsers.find(
																	(u) => u.id === section.assignedTo
																);
																const sectionFields = section.fields || [];
																return (
																	<Box key={section.id}>
																		{sIdx > 0 && <Divider sx={{ opacity: 0.15, mx: 2, my: 0.5 }} />}
																		<Box
																			sx={{
																				display: 'flex',
																				alignItems: 'center',
																				gap: 0.75,
																				px: 2,
																				pt: 0.5,
																				pb: 0.25,
																			}}>
																			<IconGroup
																				sx={{ fontSize: 14, color: theme.palette.grey[500] }}
																			/>
																			<Typography
																				sx={{
																					fontSize: '1rem',
																					fontWeight: 700,
																					color: theme.palette.grey[500],
																					textTransform: 'uppercase',
																					letterSpacing: '0.1em',
																				}}>
																				{section.name || `Section ${sIdx + 1}`}
																			</Typography>
																			{assignee && (
																				<Chip
																					label={`${assignee.firstName} ${assignee.lastName}`}
																					size="small"
																					variant="outlined"
																					sx={{ height: 18, fontSize: '0.65rem' }}
																				/>
																			)}
																			<Typography
																				sx={{
																					fontSize: '1rem',
																					fontWeight: 700,
																					color: theme.palette.grey[500],
																				}}>
																				{sectionFields.length}
																			</Typography>
																		</Box>
																		{sectionFields.map((field) => {
																			const accentColor =
																				FIELD_TYPE_COLORS[field.fieldType] || '#546e7a';
																			return (
																				<Stack
																					key={field.id}
																					direction="row"
																					alignItems="flex-start"
																					spacing={1}
																					sx={{ py: 0.5, px: 2, pl: 3.5 }}>
																					<Box
																						sx={{
																							width: 8,
																							height: '1.75rem',
																							display: 'flex',
																							alignItems: 'center',
																							flexShrink: 0,
																						}}>
																						<Box
																							sx={{
																								width: 8,
																								height: 8,
																								borderRadius: '50%',
																								bgcolor: alpha(accentColor, 0.4),
																							}}
																						/>
																					</Box>
																					<Typography
																						sx={{
																							fontSize: '1.25rem',
																							lineHeight: '1.75rem',
																							fontWeight: 500,
																							color: '#374151',
																							flex: 1,
																							minWidth: 0,
																						}}>
																						{field.label || FIELD_TYPE_LABELS[field.fieldType]}
																					</Typography>
																					<Stack
																						direction="row"
																						alignItems="center"
																						spacing={1}
																						sx={{ height: '1.75rem', flexShrink: 0 }}>
																						<Typography
																							sx={{ color: '#D1D5DB', fontSize: '1.25rem' }}>
																							—
																						</Typography>
																						<Typography
																							sx={{
																								fontSize: '1.25rem',
																								color: '#D1D5DB',
																								fontStyle: 'italic',
																							}}>
																							{field.required ? 'Required' : 'Optional'}
																						</Typography>
																						<Typography
																							sx={{
																								fontSize: '1.25rem',
																								color: accentColor,
																								fontWeight: 600,
																								opacity: 0.7,
																							}}>
																							{FIELD_TYPE_LABELS[field.fieldType] ||
																								field.fieldType}
																						</Typography>
																						{field.required ? (
																							<Box
																								sx={{
																									width: 16,
																									height: 16,
																									borderRadius: '50%',
																									border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
																								}}
																							/>
																						) : (
																							<Box sx={{ width: 16 }} />
																						)}
																					</Stack>
																				</Stack>
																			);
																		})}
																	</Box>
																);
															})}
														</Box>
													);
												})}
											</Box>
										</CardDetail>
									)}

									{/* Vouchers Card */}
									<CardDetail
										icon={IconSend}
										title="Vouchers"
										variant="financial"
										themeColor={GRADIENTS.financial.icon}>
										<Box sx={{ p: 2 }}>
											{conditionVouchers.length > 0 ? (
												<Stack spacing={1.5}>
													{conditionVouchers.map((v) => {
														const vStatus = (v.status || '').toUpperCase();
														const isPaid = vStatus === 'PAID' || vStatus === 'REDEEMED';
														return (
															<Stack
																key={v.id}
																component={RouterLink}
																to={`/project/${project?.idWbg}/spend-plans/voucher/${v.id}`}
																direction="row"
																alignItems="center"
																justifyContent="space-between"
																sx={{
																	textDecoration: 'none',
																	color: 'inherit',
																	bgcolor: alpha(GRADIENTS.financial.icon, 0.04),
																	border: '1px solid',
																	borderColor: alpha(GRADIENTS.financial.icon, 0.15),
																	borderRadius: 1.5,
																	p: 1.5,
																	cursor: 'pointer',
																	transition: 'all 0.2s',
																	'&:hover': {
																		borderColor: GRADIENTS.financial.icon,
																		boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
																	},
																}}>
																<Stack direction="row" alignItems="center" spacing={1.5}>
																	<Box
																		sx={{
																			alignItems: 'center',
																			bgcolor: isPaid
																				? alpha(COLORS.success.main, 0.12)
																				: alpha(GRADIENTS.financial.icon, 0.12),
																			borderRadius: '50%',
																			display: 'flex',
																			height: 36,
																			justifyContent: 'center',
																			width: 36,
																		}}>
																		{isPaid ? (
																			<IconCheckCircle
																				sx={{ fontSize: 18, color: COLORS.success.main }}
																			/>
																		) : (
																			<IconSend
																				sx={{ fontSize: 18, color: GRADIENTS.financial.icon }}
																			/>
																		)}
																	</Box>
																	<Box>
																		<Typography sx={{ fontWeight: 600 }} variant="body2">
																			Voucher #{v.id}
																		</Typography>
																		<Typography variant="caption" color="text.secondary">
																			{formatCurrency({
																				currency: v.currency || currency,
																				value: v.amount || 0,
																			})}
																		</Typography>
																	</Box>
																</Stack>
																{(() => {
																	const vColors = getStatusColor(v.status || 'PENDING');
																	return (
																		<Chip
																			size="small"
																			label={getStatusLabel(v.status || 'PENDING', t)}
																			sx={{
																				bgcolor: vColors.bg,
																				color: vColors.text,
																				fontWeight: 600,
																				fontSize: '1.25rem',
																			}}
																		/>
																	);
																})()}
															</Stack>
														);
													})}
												</Stack>
											) : (
												<Typography
													variant="body2"
													color="text.secondary"
													sx={{ fontStyle: 'italic', textAlign: 'center', py: 1 }}>
													No vouchers issued yet
												</Typography>
											)}

											{/* Multi-voucher / multi-currency reconciliation (Story #88) */}
											{conditionVouchers.length > 0 && (
												<Box sx={{ mt: 2 }}>
													<VoucherPaymentReconciliation
														vouchers={conditionVouchers}
														reportingCurrency={
															project?.currencies?.find((c) => c.type === 'REPORTING')?.currency ||
															currency
														}
													/>
												</Box>
											)}
										</Box>
									</CardDetail>
								</Stack>
							</Grid>

							{/* Right Column: Payment Info + Context + History */}
							<Grid item xs={12} lg={4}>
								<Stack spacing={2}>
									{/* Linked Context Card */}
									<CardDetail
										icon={IconLink}
										title={t('spendPlan.linkedTo')}
										variant="condition"
										themeColor={GRADIENTS.condition.icon}
										emptyMessage={t('spendPlan.condition.notLinked')}>
										<Box sx={{ p: 2 }}>
											<Stack spacing={1.5}>
												{/* Work Plan */}
												{workPlan && (
													<Stack
														alignItems="center"
														direction="row"
														justifyContent="space-between"
														onClick={() => handleOpenWorkPlanPage(workPlan.id)}
														sx={{
															bgcolor: alpha(GRADIENTS.workPlan.icon, 0.04),
															border: '1px solid',
															borderColor: alpha(GRADIENTS.workPlan.icon, 0.2),
															borderRadius: 1.5,
															cursor: 'pointer',
															p: 1.5,
															transition: 'all 0.2s',
															'&:hover': {
																borderColor: GRADIENTS.workPlan.icon,
																boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
															},
														}}>
														<Stack alignItems="center" direction="row" spacing={1.5}>
															<Box
																sx={{
																	alignItems: 'center',
																	bgcolor: alpha(GRADIENTS.workPlan.icon, 0.12),
																	borderRadius: '50%',
																	display: 'flex',
																	height: { xs: 36, sm: 40 },
																	justifyContent: 'center',
																	width: { xs: 36, sm: 40 },
																}}>
																<IconWorkPlan
																	sx={{
																		color: GRADIENTS.workPlan.icon,
																		fontSize: { xs: 18, sm: 20 },
																	}}
																/>
															</Box>
															<Box>
																<Typography
																	color="text.secondary"
																	variant="caption"
																	display="block">
																	{t('spendPlan.workPlan')}
																</Typography>
																<Typography sx={{ fontWeight: 600 }} variant="body2">
																	{workPlan.name}
																</Typography>
															</Box>
														</Stack>
														<IconOpenInNew sx={{ color: GRADIENTS.workPlan.icon, fontSize: 16 }} />
													</Stack>
												)}

												{/* Activity */}
												{activity && (
													<Stack
														alignItems="center"
														direction="row"
														justifyContent="space-between"
														onClick={() => handleOpenActivityPage(activity.id)}
														sx={{
															bgcolor: alpha(GRADIENTS.activity.icon, 0.04),
															border: '1px solid',
															borderColor: alpha(GRADIENTS.activity.icon, 0.2),
															borderRadius: 1.5,
															cursor: 'pointer',
															p: 1.5,
															transition: 'all 0.2s',
															'&:hover': {
																borderColor: GRADIENTS.activity.icon,
																boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
															},
														}}>
														<Stack alignItems="center" direction="row" spacing={1.5}>
															<Box
																sx={{
																	alignItems: 'center',
																	bgcolor: alpha(GRADIENTS.activity.icon, 0.12),
																	borderRadius: '50%',
																	display: 'flex',
																	height: { xs: 36, sm: 40 },
																	justifyContent: 'center',
																	width: { xs: 36, sm: 40 },
																}}>
																<IconActivity
																	sx={{
																		color: GRADIENTS.activity.icon,
																		fontSize: { xs: 18, sm: 20 },
																	}}
																/>
															</Box>
															<Box>
																<Typography
																	color="text.secondary"
																	variant="caption"
																	display="block">
																	{t('spendPlan.activity')}
																</Typography>
																<Typography sx={{ fontWeight: 600 }} variant="body2">
																	{activity.name}
																</Typography>
															</Box>
														</Stack>
														<IconOpenInNew sx={{ color: GRADIENTS.activity.icon, fontSize: 16 }} />
													</Stack>
												)}

												{/* Contract */}
												{contract && (
													<Stack
														alignItems="center"
														direction="row"
														justifyContent="space-between"
														onClick={() => handleOpenContractPage(contract.id)}
														sx={{
															bgcolor: alpha(GRADIENTS.contract.icon, 0.04),
															border: '1px solid',
															borderColor: alpha(GRADIENTS.contract.icon, 0.2),
															borderRadius: 1.5,
															cursor: 'pointer',
															p: 1.5,
															transition: 'all 0.2s',
															'&:hover': {
																borderColor: GRADIENTS.contract.icon,
																boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
															},
														}}>
														<Stack alignItems="center" direction="row" spacing={1.5}>
															<Box
																sx={{
																	alignItems: 'center',
																	bgcolor: alpha(GRADIENTS.contract.icon, 0.12),
																	borderRadius: '50%',
																	display: 'flex',
																	height: { xs: 36, sm: 40 },
																	justifyContent: 'center',
																	width: { xs: 36, sm: 40 },
																}}>
																<IconContract
																	sx={{
																		color: GRADIENTS.contract.icon,
																		fontSize: { xs: 18, sm: 20 },
																	}}
																/>
															</Box>
															<Box>
																<Typography
																	color="text.secondary"
																	variant="caption"
																	display="block">
																	{t('spendPlan.contract')}
																</Typography>
																<Typography sx={{ fontWeight: 600 }} variant="body2">
																	{getContractNum(contract)} - {getContractSupplierName(contract)}
																</Typography>
															</Box>
														</Stack>
														<IconOpenInNew sx={{ color: GRADIENTS.contract.icon, fontSize: 16 }} />
													</Stack>
												)}

												{/* No linked context */}
												{!workPlan && !activity && !contract && (
													<Typography
														variant="body2"
														color="text.secondary"
														sx={{ fontStyle: 'italic', textAlign: 'center', py: 1 }}>
														{t('spendPlan.condition.notLinked')}
													</Typography>
												)}

												{/* Classification — preconfigured component/category/expense type */}
												{(condition.component || condition.category || condition.expenseType) && (
													<>
														{(workPlan || activity || contract) && <Divider sx={{ my: 0.5 }} />}
														<Typography
															variant="caption"
															color="text.secondary"
															sx={{
																fontWeight: 600,
																textTransform: 'uppercase',
																letterSpacing: '0.05em',
																px: 0.5,
															}}>
															Additional Requirements
														</Typography>
														{condition.component && typeof condition.component === 'object' && (
															<Stack
																alignItems="center"
																direction="row"
																spacing={1.5}
																sx={{
																	bgcolor: alpha('#7c3aed', 0.04),
																	border: '1px solid',
																	borderColor: alpha('#7c3aed', 0.2),
																	borderRadius: 1.5,
																	p: 1.5,
																}}>
																<Box
																	sx={{
																		alignItems: 'center',
																		bgcolor: alpha('#7c3aed', 0.12),
																		borderRadius: '50%',
																		display: 'flex',
																		height: 36,
																		justifyContent: 'center',
																		width: 36,
																	}}>
																	<IconComponent sx={{ color: '#7c3aed', fontSize: 18 }} />
																</Box>
																<Box>
																	<Typography
																		color="text.secondary"
																		variant="caption"
																		display="block">
																		Component
																	</Typography>
																	<Typography sx={{ fontWeight: 600 }} variant="body2">
																		{(condition.component as { name?: string }).name}
																	</Typography>
																</Box>
															</Stack>
														)}
														{condition.category && typeof condition.category === 'object' && (
															<Stack
																alignItems="center"
																direction="row"
																spacing={1.5}
																sx={{
																	bgcolor: alpha('#d97706', 0.04),
																	border: '1px solid',
																	borderColor: alpha('#d97706', 0.2),
																	borderRadius: 1.5,
																	p: 1.5,
																}}>
																<Box
																	sx={{
																		alignItems: 'center',
																		bgcolor: alpha('#d97706', 0.12),
																		borderRadius: '50%',
																		display: 'flex',
																		height: 36,
																		justifyContent: 'center',
																		width: 36,
																	}}>
																	<IconCategory sx={{ color: '#d97706', fontSize: 18 }} />
																</Box>
																<Box>
																	<Typography
																		color="text.secondary"
																		variant="caption"
																		display="block">
																		Category
																	</Typography>
																	<Typography sx={{ fontWeight: 600 }} variant="body2">
																		{(condition.category as { name?: string }).name}
																	</Typography>
																</Box>
															</Stack>
														)}
														{condition.expenseType && typeof condition.expenseType === 'object' && (
															<Stack
																alignItems="center"
																direction="row"
																spacing={1.5}
																sx={{
																	bgcolor: alpha('#0d9488', 0.04),
																	border: '1px solid',
																	borderColor: alpha('#0d9488', 0.2),
																	borderRadius: 1.5,
																	p: 1.5,
																}}>
																<Box
																	sx={{
																		alignItems: 'center',
																		bgcolor: alpha('#0d9488', 0.12),
																		borderRadius: '50%',
																		display: 'flex',
																		height: 36,
																		justifyContent: 'center',
																		width: 36,
																	}}>
																	<IconExpenseType sx={{ color: '#0d9488', fontSize: 18 }} />
																</Box>
																<Box>
																	<Typography
																		color="text.secondary"
																		variant="caption"
																		display="block">
																		Expense Type
																	</Typography>
																	<Typography sx={{ fontWeight: 600 }} variant="body2">
																		{(condition.expenseType as { name?: string }).name}
																	</Typography>
																</Box>
															</Stack>
														)}
													</>
												)}
											</Stack>
										</Box>
									</CardDetail>

									{/* Payment Info Card */}
									{isPaymentCondition && contract && (
										<CardDetail
											icon={IconPayment}
											title={t('spendPlan.condition.paymentDetails')}
											variant="financial"
											themeColor={GRADIENTS.financial.icon}>
											<Box sx={{ p: 2 }}>
												<Stack spacing={1.5}>
													<Stack direction="row" justifyContent="space-between" alignItems="center">
														<Typography color="text.secondary" variant="body2">
															{t('spendPlan.condition.contractValue')}
														</Typography>
														<Typography sx={{ fontWeight: 600 }} variant="body2">
															{formatCurrency({ currency, value: contractValue })}
														</Typography>
													</Stack>
													{condition.paymentPercentage && (
														<Stack
															direction="row"
															justifyContent="space-between"
															alignItems="center">
															<Typography color="text.secondary" variant="body2">
																{t('spendPlan.condition.paymentPercentage')}
															</Typography>
															<Typography
																sx={{ fontWeight: 600, color: COLORS.info.main }}
																variant="body2">
																{condition.paymentPercentage}%
															</Typography>
														</Stack>
													)}
													{condition.milestoneType && (
														<Stack
															direction="row"
															justifyContent="space-between"
															alignItems="center">
															<Typography color="text.secondary" variant="body2">
																Milestone Type
															</Typography>
															<Typography sx={{ fontWeight: 600 }} variant="body2">
																{condition.milestoneType.charAt(0) +
																	condition.milestoneType.slice(1).toLowerCase()}
															</Typography>
														</Stack>
													)}
													<Divider />
													<Stack direction="row" justifyContent="space-between" alignItems="center">
														<Typography
															color="text.secondary"
															variant="body2"
															sx={{ fontWeight: 500 }}>
															{t('spendPlan.condition.paymentAmount')}
														</Typography>
														<Typography
															sx={{ fontWeight: 700, color: COLORS.info.main }}
															variant="body1">
															{formatCurrency({ currency, value: calculatedPaymentAmount })}
														</Typography>
													</Stack>
												</Stack>
											</Box>
										</CardDetail>
									)}
								</Stack>
							</Grid>
						</Grid>

						{/* Edit Condition Dialog */}
						{condition && (
							<DialogAddCondition
								condition={condition}
								isOpen={
									searchParams.get('dialog') === 'editCondition' && !conditionLoading && !!condition
								}
								onClose={() => {
									const params = new URLSearchParams(searchParams);
									params.delete('dialog');
									params.delete('dialogCondition');
									setSearchParams(params);
								}}
								onSuccess={() => {
									const params = new URLSearchParams(searchParams);
									params.delete('dialog');
									params.delete('dialogCondition');
									setSearchParams(params);
									refetch(); // Refetch to get updated condition
								}}
							/>
						)}
					</>
				) : null}
			</Box>
		</Box>
	);
};
